import { useGetShopByIdQuery, useGetShopRatingQuery, useGetFoodItemsQuery, useLazySearchFoodItemsQuery, usePostSuggestionMutation } from '../features/shop/shopApiSlice';
import { useGetUserInfoQuery } from '../features/user/userApiSlice';
import { useState, useCallback } from 'react';

/**
 * Hook to fetch shop data by ID
 * @param {string} shopId - The shop ID
 * @returns {object} Shop data, loading, and error states
 */
export const useShopData = (shopId) => {
  const { data, isLoading, error } = useGetShopByIdQuery(shopId, {
    skip: !shopId,
  });

  const shopData = data?.data?.Shop || null;

  return {
    shopData,
    isLoading,
    error,
    hasImage: !!shopData?.image,
  };
};

/**
 * Hook to fetch shop rating
 * @param {string} shopId - The shop ID
 * @returns {object} Rating data, loading, and error states
 */
export const useShopRating = (shopId) => {
  const { data, isLoading, error } = useGetShopRatingQuery(shopId, {
    skip: !shopId,
  });

  const rating = data?.data?.averageRating || 0;

  return {
    rating,
    isLoading,
    error,
  };
};

/**
 * Hook to fetch food items for a shop
 * @param {string} shopId - The shop ID
 * @returns {object} Food items, loading, and error states
 */
export const useFoodItems = (shopId) => {
  const { data, isLoading, error } = useGetFoodItemsQuery(shopId, {
    skip: !shopId,
  });

  // Handle different response structures from API
  let foodItems = [];
  if (data) {
    // Check for categorized format from new backend
    if (data.regular || data.specialOffers || data.salespromotion) {
      const allItems = [
        ...(data.regular || []),
        ...(data.specialOffers || []),
        ...(data.salespromotion || [])
      ];
      // Deduplicate items that appear in multiple categories
      const uniqueIds = new Set();
      foodItems = allItems.filter(item => {
        if (uniqueIds.has(item._id)) return false;
        uniqueIds.add(item._id);
        return true;
      });
    }
    // Try data.data first (expected structure)
    else if (Array.isArray(data.data)) {
      foodItems = data.data;
    } 
    // Try data itself if it's an array
    else if (Array.isArray(data)) {
      foodItems = data;
    }
    // Try data.items or similar nested structures
    else if (data.items && Array.isArray(data.items)) {
      foodItems = data.items;
    }
  }

  return {
    foodItems,
    isLoading,
    error,
  };
};

/**
 * Hook to search food items by name
 * @returns {object} Search function, results, loading, and error states
 */
export const useFoodItemSearch = () => {
  const [searchFoodItems, { data, isLoading }] = useLazySearchFoodItemsQuery();
  const [searchResults, setSearchResults] = useState({
    regular: [],
    specialoffer: [],
    salespromotion: [],
  });

  const handleSearch = useCallback((dishname, businessType = 'foodShopItem') => {
    if (!dishname || dishname.trim() === '') {
      setSearchResults({ regular: [], specialoffer: [], salespromotion: [] });
      return;
    }

    searchFoodItems({ dishname, businessType }).then((result) => {
      if (result.data?.data) {
        setSearchResults({
          regular: result.data.data.regular || [],
          specialoffer: result.data.data.specialoffer || [],
          salespromotion: result.data.data.salespromotion || [],
        });
      }
    });
  }, [searchFoodItems]);

  return {
    handleSearch,
    searchResults,
    isLoading,
  };
};

/**
 * Hook to fetch user info
 * @param {string} userId - The user ID
 * @returns {object} User data, loading, and error states
 */
export const useUserData = (userId) => {
  const { data, isLoading, error } = useGetUserInfoQuery(userId, {
    skip: !userId,
  });

  const userData = data?.data?.User || null;

  return {
    userData,
    isLoading,
    error,
    hasImage: !!userData?.imageUrl,
  };
};

/**
 * Hook to post suggestion to shop owner
 * @returns {object} Mutation function, loading, and error states
 */
export const useSuggestion = () => {
  const [postSuggestion, { isLoading, error }] = usePostSuggestionMutation();

  const handlePostSuggestion = useCallback(async (suggestionData) => {
    try {
      const result = await postSuggestion(suggestionData).unwrap();
      return {
        success: true,
        data: result,
      };
    } catch (err) {
      return {
        success: false,
        error: err?.data?.message || 'Failed to post suggestion',
      };
    }
  }, [postSuggestion]);

  return {
    handlePostSuggestion,
    isLoading,
    error,
  };
};
